import * as React from 'react';

import { COLORS } from '@xing-com/crate-lebenslauf-constants';
import { usePagination } from '@xing-com/crate-lebenslauf-helpers';
import {
  useIsCoverLetterEditor,
  useReadCoverLetter,
  useReadVitaReactiveVariable,
} from '@xing-com/crate-lebenslauf-hooks';

import {
  VitaDocumentLanguageProvider,
  type Translations,
} from '../vita-document-language-provider';

import {
  TemplateContainer,
  TemplateBackground,
  PageContainer,
  PageInnerContainer,
  ColumnContainer,
  Column,
} from './template.styled';

export type TemplateConfig = {
  columnSize: string[];
  columnSpacing?: string;
  columnBg?: string[];
  columnMargin?: number;
  pageMargin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
};

type TemplateProps = {
  unitsByColumn: JSX.Element[][];
  Header: React.VFC<{ pageIndex: number }>;
  pageVisible?: number;
  setTotalPages?: (total: number) => void;
  templateConfig?: TemplateConfig;
  translations: Translations;
  border?: string;
};

export const Template: React.FC<TemplateProps> = ({
  unitsByColumn,
  pageVisible,
  templateConfig,
  setTotalPages,
  Header,
  translations,
  border,
}) => {
  const firstPageRef = React.useRef<HTMLDivElement>(null);
  const firstColumnContainerRef = React.useRef<HTMLDivElement>(null);
  const { theme } = useReadVitaReactiveVariable();
  const coverLetter = useReadCoverLetter();
  const isCoverLetterEditor = useIsCoverLetterEditor();
  const unitsByPageAndColumn = usePagination(
    unitsByColumn,
    firstPageRef,
    firstColumnContainerRef,
    setTotalPages
  );

  const color = isCoverLetterEditor ? coverLetter.theme?.color : theme?.color;

  return (
    <>
      <TemplateContainer
        $colors={COLORS[color || 'DARK_BLUE']}
        $pageMargin={templateConfig?.pageMargin}
        $border={Boolean(border)}
      >
        <VitaDocumentLanguageProvider translations={translations}>
          <TemplateBackground />
          {unitsByPageAndColumn?.length ? (
            unitsByPageAndColumn?.map((unitsByColumn, pageIndex) => {
              return (
                <PageContainer
                  $visible={
                    typeof pageVisible === 'undefined' ||
                    pageIndex === pageVisible
                  }
                  $showSinglePage={typeof pageVisible !== 'undefined'}
                  $border={border}
                  key={`${pageIndex}`}
                >
                  <PageInnerContainer
                    ref={pageIndex === 0 ? firstPageRef : null}
                  >
                    <Header pageIndex={pageIndex} />
                    <ColumnContainer
                      ref={pageIndex === 0 ? firstColumnContainerRef : null}
                    >
                      {unitsByColumn.map((columnUnits, columnIndex) => {
                        return (
                          <Column
                            key={columnIndex}
                            $columnMargin={templateConfig?.columnMargin}
                            $columnSpacing={templateConfig?.columnSpacing}
                            $columnSize={
                              templateConfig?.columnSize[columnIndex]
                            }
                          >
                            {columnUnits}
                          </Column>
                        );
                      })}
                    </ColumnContainer>
                  </PageInnerContainer>
                </PageContainer>
              );
            })
          ) : (
            <PageContainer $visible $showSinglePage>
              <Header pageIndex={0} />
            </PageContainer>
          )}
        </VitaDocumentLanguageProvider>
      </TemplateContainer>
    </>
  );
};
